"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UpdateCollector = void 0;
const domain_1 = require("../../domain");
const utils_1 = require("../../utils");
const lodash_1 = require("lodash");
const groupInvite_1 = require("../../domain/groupInvite");
const lessonModule_1 = require("../../domain/lessonModule");
const ratings_1 = require("../../domain/ratings");
class UpdateCollector {
    constructor(batch, refs, logger) {
        this.logger = logger;
        this.pending = {};
        this.updatedDocuments = {
            collection: new Set(),
            id: new Set(),
        };
        this.refs = refs;
        this.batch = batch;
    }
    logReference(...rows) {
        rows.forEach(([collection, crud, id]) => {
            this.updatedDocuments.collection.add(collection);
            this.updatedDocuments.id.add(`${collection}/${id}`);
        });
    }
    updatedDocs() {
        return {
            collection: Array.from(this.updatedDocuments.collection.values()),
            id: Array.from(this.updatedDocuments.id.values()),
        };
    }
    // INVITES
    setInvite(invite) {
        this.logReference(['invites', 'set', invite._id]);
        this.batch.set(this.refs.inviteDocument(invite._id), (0, utils_1.omitDeepUndefined)(invite), { merge: true });
    }
    deleteInvite(id) {
        this.logReference(['invites', 'delete', id]);
        this.batch.delete(this.refs.inviteDocument(id));
    }
    // TEASERS
    setBookmark(bookmark) {
        this.logReference(['bookmarks', 'set', bookmark._id]);
        this.batch.set(this.refs.bookmarkDocument(bookmark._id), (0, utils_1.omitDeepUndefined)(bookmark), { merge: true });
    }
    updateBookmark(id, data) {
        this.logReference(['bookmarks', 'set', id]);
        const updateFields = domain_1.BookmarkUpdateData.updateFields(data);
        if (!(0, lodash_1.isEmpty)(updateFields)) {
            this.batch.update(this.refs.bookmarkDocument(id), updateFields);
        }
    }
    deleteBookmark(id) {
        this.logReference(['bookmarks', 'delete', id]);
        this.batch.delete(this.refs.bookmarkDocument(id));
    }
    // RATINGS
    setRating(rating) {
        this.logReference(['ratings', 'set', rating._id]);
        this.batch.set(this.refs.rating(rating._id), (0, utils_1.omitDeepUndefined)(rating), { merge: true });
    }
    updateRating(id, data) {
        this.logReference(['ratings', 'update', id]);
        const updateFields = ratings_1.RatingUpdateData.updateFields(data);
        if (!(0, lodash_1.isEmpty)(updateFields)) {
            this.batch.update(this.refs.rating(id), updateFields);
        }
    }
    deleteRating(id) {
        this.logReference(['ratings', 'delete', id]);
        this.batch.delete(this.refs.rating(id));
    }
    // ASSIGNMENT
    setAssignmentContent(assignment) {
        this.logReference(['assignments', 'set', assignment._id]);
        this.batch.set(this.refs.assignmentContentDocument(assignment._id), (0, utils_1.omitDeepUndefined)(assignment));
    }
    setAssignmentMeta(meta) {
        this.logReference(['assignmentMetas', 'set', meta._id]);
        this.batch.set(this.refs.assignmentMetaDocument(meta._id), (0, utils_1.omitDeepUndefined)(meta));
    }
    setAssignmentSettings(settings) {
        this.logReference(['assignmentSettings', 'set', settings._id]);
        this.batch.set(this.refs.assignmentSettings(settings._id), (0, utils_1.omitDeepUndefined)(settings));
    }
    setAssignmentTeacherEntry(teacherEntry) {
        this.batch.set(this.refs.teacherEntry(teacherEntry._id), (0, utils_1.omitDeepUndefined)(teacherEntry));
    }
    setAssignmentComplete(assignmentData) {
        this.setAssignmentContent(assignmentData.content);
        this.setAssignmentMeta(assignmentData.meta);
        this.setAssignmentSettings(assignmentData.settings);
        if (assignmentData.teacherEntry) {
            this.setAssignmentTeacherEntry(assignmentData.teacherEntry);
        }
    }
    updateAssignmentMeta(id, data, merge = false) {
        this.logReference(['assignmentMetas', 'update', id]);
        const ref = this.refs.assignmentMetaDocument(id);
        const updateFields = domain_1.AssignmentMeta.updateFields(data);
        if ((0, lodash_1.isEmpty)(updateFields)) {
            const error = new domain_1.AppError('invalid-params', 'Unable to find fields to update metas for assignment', {
                assignmentId: id,
            });
            // Only notify devs that this function was called with empty parameters.
            this.logger.warn(error);
        }
        else {
            if (!merge) {
                this.batch.update(ref, (0, utils_1.omitDeepUndefined)(updateFields));
            }
            else {
                this.addToPending(ref, (0, utils_1.omitDeepUndefined)(updateFields));
            }
        }
    }
    updateAssignmentContent(id, data) {
        this.logReference(['assignments', 'update', id]);
        const updateFields = domain_1.AssignmentContent.updateFields(data);
        if (!(0, lodash_1.isEmpty)(updateFields)) {
            this.batch.update(this.refs.assignmentMetaDocument(id), (0, utils_1.omitDeepUndefined)(updateFields));
        }
    }
    updateAssignmentSettings(id, data) {
        this.logReference(['assignmentSettings', 'update', id]);
        const updateFields = domain_1.AssignmentSettings.updateFields(data);
        if ((0, lodash_1.isEmpty)(updateFields)) {
            const error = new domain_1.AppError('not-found', 'Unable to find fields to update for assignment', { assignmentId: id });
            // Only notify devs that this function was called with empty parameters.
            this.logger.warn(error);
        }
        else {
            this.batch.update(this.refs.assignmentSettings(id), (0, utils_1.omitDeepUndefined)(updateFields));
        }
    }
    deleteAssignmentData(id) {
        this.logReference(['assignments', 'delete', id], ['assignmentMetas', 'delete', id], ['assignmentSettings', 'delete', id]);
        return this.deleteAssignmentDataByRef(id, this.refs.assignmentMetaDocument(id));
    }
    deleteArchivedAssignmentData(id) {
        this.logReference(['assignments', 'delete', id], ['assignmentMetasArchive', 'delete', id], ['assignmentSettings', 'delete', id]);
        return this.deleteAssignmentDataByRef(id, this.refs.assignmentMetaArchiveDocument(id));
    }
    deleteAssignmentDataByRef(id, assignmentMetaDocument) {
        this.batch.delete(this.refs.assignmentSettings(id));
        this.batch.delete(this.refs.assignmentContentDocument(id));
        this.batch.delete(assignmentMetaDocument);
    }
    // ARCHIVING
    deleteAssignmentMeta(id) {
        this.logReference(['assignmentMetas', 'delete', id]);
        this.batch.delete(this.refs.assignmentMetaDocument(id));
    }
    setAssignmentMetaArchiveRecord(meta) {
        this.logReference(['assignmentMetasArchive', 'set', meta._id]);
        this.batch.set(this.refs.assignmentMetaArchiveDocument(meta._id), meta);
    }
    deleteAssignmentMetaArchiveRecord(id) {
        this.logReference(['assignmentMetasArchive', 'delete', id]);
        this.batch.delete(this.refs.assignmentMetaArchiveDocument(id));
    }
    // ENTRY
    deleteAssignmentEntry(id) {
        this.logReference(['entries', 'delete', id]);
        this.batch.delete(this.refs.entry(id));
    }
    setAssignmentEntry(entry) {
        this.logReference(['entries', 'set', entry._id]);
        this.batch.set(this.refs.entry(entry._id), (0, utils_1.omitDeepUndefined)(entry), { merge: false });
    }
    updateAssignmentEntry(id, data, unset) {
        this.logReference(['entries', 'update', id]);
        const updateFields = domain_1.AssignmentEntryUpdateData.updateFields(data, unset);
        if (!(0, lodash_1.isEmpty)(updateFields)) {
            this.batch.update(this.refs.entry(id), updateFields);
        }
    }
    updateTeacherEntry(assignmentId, pinId, dataUpdate) {
        const updateFields = dataUpdate;
        if ((0, lodash_1.isEmpty)(updateFields)) {
            const error = new domain_1.AppError('not-found', 'Unable to find fields to update for assignment', { assignmentId });
            // Only notify devs that this function was called with empty parameters.
            this.logger.warn(error);
        }
        else {
            this.batch.update(this.refs.teacherEntry(assignmentId), (0, utils_1.omitDeepUndefined)(updateFields));
        }
    }
    // GROUPINVITES
    deleteGroupInvite(id) {
        this.logReference(['groupInvites', 'delete', `${id}`]);
        this.batch.delete(this.refs.groupInvite(id));
    }
    updateGroupInvite(id, data) {
        this.logReference(['groupInvites', 'update', `${id}`]);
        const ref = this.refs.groupInvite(id);
        const updateFields = groupInvite_1.GroupInvite.updateFields(data);
        this.batch.update(ref, updateFields);
    }
    // MERGE LOGIC
    addToPending(ref, data) {
        const record = this.pending[ref.path];
        if (!record) {
            this.pending[ref.path] = {
                ref,
                data,
            };
        }
        else {
            record.data = Object.assign({}, record.data, data);
        }
    }
    commitAllPendingMerges() {
        Object.values(this.pending).forEach((pending) => {
            this.batch.update(pending.ref, (0, utils_1.omitDeepUndefined)(pending.data));
        });
    }
    setLessonModule(module) {
        this.batch.set(this.refs.lessonModule(module.groupId, module.id), (0, utils_1.omitDeepUndefined)(module));
    }
    updateLessonModule(id, groupId, update) {
        this.batch.update(this.refs.lessonModule(groupId, id), (0, lessonModule_1.updateLessonModuleUpdateFields)(update));
    }
    deleteModule(groupId, moduleId) {
        this.batch.delete(this.refs.lessonModule(groupId, moduleId));
    }
    setActivity(groupId, activity) {
        this.batch.set(this.refs.studentActivity(groupId, activity.user), (0, utils_1.omitDeepUndefined)(activity));
    }
    pushEntryLogin(entry) {
        // cant use firebase.firestore.FieldValue.arrayUnion with the current admin and firestore combo
        const logins = entry.logins || [];
        logins.push({ ts: Date.now() });
        if (logins.length > domain_1.AssignmentEntry.maxRegisteredLoginCount)
            return;
        this.batch.update(this.refs.entry(entry._id), {
            logins,
        });
    }
}
exports.UpdateCollector = UpdateCollector;
//# sourceMappingURL=UpdateCollector.js.map